/**
 * Pure validators for the Task wizard and upload modal.
 *
 * The wizard auto-derives JSON output keys for single-type tasks and collects
 * sub-task names for composite tasks. This module validates the task-level
 * shape plus the per-objective upload-modal state.
 */

/** Maximum number of task types allowed inside a single Task. */
export const MAX_OBJECTIVES_PER_TASK = 8;

/** Maximum allowed length for a Task Name (sub_group_name). */
export const MAX_TASK_NAME_LEN = 64;

/** Maximum allowed length for a multi-type sub-task name (usecase_name). */
export const MAX_SUB_TASK_NAME_LEN = 64;

/**
 * Validate the TaskComposer shape used by `CreateProjectModal` and
 * `NewTaskModal`. The user-facing wizard now owns:
 *
 *   - taskName (required, becomes sub_group_name)
 *   - taskTypes (1..MAX, each with type, languages, and — for classification — a subtype)
 *
 * Per-row JSON output keys are auto-derived for single-type tasks and required
 * as sub-task names once there is more than one task type.
 *
 * @param {{ taskName: string, taskTypes: Array<Object> }} task
 * @returns {{
 *   ok: boolean,
 *   errors: { taskName?: string, taskTypes: Object<number, Object> },
 *   summary: string | null,
 * }}
 */
export const validateTask = (task) => {
    const errors = { taskTypes: {} };
    const summaries = [];

    const trimmedName = (task?.taskName || '').trim();
    if (!trimmedName) {
        errors.taskName = 'Task Name is required';
    } else if (trimmedName.length > MAX_TASK_NAME_LEN) {
        errors.taskName = `Task Name must be ${MAX_TASK_NAME_LEN} characters or fewer`;
    }

    const rows = Array.isArray(task?.taskTypes) ? task.taskTypes : [];
    if (rows.length === 0) {
        summaries.push('Add at least one sub-task');
    } else if (rows.length > MAX_OBJECTIVES_PER_TASK) {
        summaries.push(`A task can have at most ${MAX_OBJECTIVES_PER_TASK} sub-tasks`);
    }

    const isMultiTypeTask = rows.length > 1;
    const seenSubTaskNames = new Set();

    rows.forEach((row, idx) => {
        const rowErr = {};
        if (isMultiTypeTask) {
            const subTaskName = (row.subTaskName || '').trim().replace(/\s/g, '-').replace(/-+$/, '');
            const normalizedSubTaskName = subTaskName.toLowerCase();
            if (!subTaskName) {
                rowErr.subTaskName = 'Sub-task name is required';
            } else if (subTaskName.length > MAX_SUB_TASK_NAME_LEN) {
                rowErr.subTaskName = `Sub-task name must be ${MAX_SUB_TASK_NAME_LEN} characters or fewer`;
            } else if (seenSubTaskNames.has(normalizedSubTaskName)) {
                rowErr.subTaskName = 'Sub-task names must be unique';
            } else {
                seenSubTaskNames.add(normalizedSubTaskName);
            }
        }
        if (!row.type) {
            rowErr.type = 'Task Type is required';
        }
        if (row.type === 'ClassificationResponse' && !row.classificationType) {
            rowErr.classificationType = 'Select a classification type';
        }
        if (!Array.isArray(row.languages) || row.languages.length === 0) {
            rowErr.languages = 'At least one language is required';
        }
        if (Object.keys(rowErr).length > 0) {
            errors.taskTypes[row.id ?? idx] = rowErr;
        }
    });

    const rowErrCount = Object.keys(errors.taskTypes).length;
    if (rowErrCount > 0) {
        summaries.push(
            rowErrCount === 1
                ? 'One sub-task has missing or invalid fields'
                : `${rowErrCount} sub-tasks have missing or invalid fields`
        );
    }

    const ok =
        !errors.taskName &&
        rowErrCount === 0 &&
        rows.length >= 1 &&
        rows.length <= MAX_OBJECTIVES_PER_TASK;

    return {
        ok,
        errors,
        summary: ok ? null : summaries.join('. ') || 'Please fix the highlighted fields',
    };
};

/**
 * Per-objective upload-modal column-selection state validator.
 *
 * Three outcomes:
 *   - `{ kind: 'ok' }`         — the tab is complete (or explicitly opted out).
 *   - `{ kind: 'soft-empty' }` — column not selected, but the user can still
 *                                proceed via the "Are you sure you want to
 *                                upload a file without any outputs?" confirm
 *                                dialog. This used to be a hard error.
 *   - `{ kind: 'hard', message }` — non-recoverable error that must block
 *                                   submit (e.g. invalid label characters).
 *
 * When `entry.hasLabels === false`:
 *   - unlabeled → ok (no response column)
 *   - generate_with_ai + class/NER → hard error until aiLabels has ≥1 name
 *   - generate_with_ai + generative → ok (no catalog)
 *
 * @param {Object} entry - per-output state slice
 * @param {string} taskType
 * @returns {{ kind: 'ok' } | { kind: 'soft-empty' } | { kind: 'hard', message: string }}
 */
export const validateColumnSelection = (entry, taskType) => {
    if (!entry || !entry.hasData) {
        return { kind: 'ok' };
    }

    if (entry.hasLabels === false) {
        const mode = entry.noLabelsMode || 'generate_with_ai';
        if (mode === 'unlabeled') return { kind: 'ok' };
        const needsCatalog =
            taskType === 'ClassificationResponse' ||
            taskType === 'NERResponse' ||
            taskType === 'EntityRecognitionResponse';
        if (needsCatalog) {
            const rows = entry.aiLabels || [];
            const hasName = rows.some((r) => (r?.name || '').trim());
            if (!hasName) {
                return {
                    kind: 'hard',
                    message:
                        taskType === 'ClassificationResponse'
                            ? 'Add at least one label for AI generation'
                            : 'Add at least one entity type for AI generation',
                };
            }
        }
        return { kind: 'ok' };
    }

    if (taskType === 'ClassificationResponse') {
        if (!entry.labelColumn) return { kind: 'soft-empty' };
        return { kind: 'ok' };
    }

    if (taskType === 'NERResponse' || taskType === 'EntityRecognitionResponse') {
        if (!entry.labelColumn) return { kind: 'soft-empty' };
        return { kind: 'ok' };
    }

    if (taskType === 'Summarization' || taskType === 'ContentGeneration') {
        // Forms store generative column on labelColumn and/or summaryColumn.
        if (!entry.summaryColumn && !entry.labelColumn) return { kind: 'soft-empty' };
        return { kind: 'ok' };
    }

    return { kind: 'ok' };
};

/**
 * Validate the per-output map produced by the upload modal.
 *
 * @param {Object<string, Object>} perOutput - keyed by usecase _id.
 * @param {Array<Object>} usecases - usecase rows for this task (gives us task_type per id).
 * @returns {{
 *   ok: boolean,                       // no hard errors
 *   anyEmpty: boolean,                 // any tab is `soft-empty`; trigger confirm step
 *   errors: Object<string, string>,    // hard errors only — drives tab red dots and inline messages
 *   softErrors: Object<string, true>,  // tabs whose column is missing (used for soft UX hints if needed)
 * }}
 */
export const validatePerOutput = (perOutput, usecases = []) => {
    const errors = {};
    const softErrors = {};
    let anyEmpty = false;
    usecases.forEach((uc) => {
        const id = String(uc._id);
        const entry = perOutput?.[id];
        const result = validateColumnSelection(entry, uc.task_type);
        if (result.kind === 'hard') {
            errors[id] = result.message;
        } else if (result.kind === 'soft-empty') {
            anyEmpty = true;
            softErrors[id] = true;
        }
    });
    return { ok: Object.keys(errors).length === 0, anyEmpty, errors, softErrors };
};
